import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE_MB,
  EXCLUDED_WORDS,
} from "./constants";

/**
 * Utility functions for product add/edit operations
 */

/**
 * Capitalize text with proper formatting
 * @param {string} text - Text to capitalize
 * @returns {string} Capitalized text
 */
export const capitalizeText = (text) => {
  return text
    .split(" ")
    .map((word, index) => {
      if (EXCLUDED_WORDS.includes(word.toLowerCase()) && index !== 0) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};

/**
 * Validate image file
 * @param {File} file - File to validate
 * @returns {Object} Validation result
 */
export const validateImageFile = (file) => {
  const errors = {};

  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    errors.image = "Invalid file format. Only JPG, PNG, and GIF are allowed.";
  }

  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > MAX_FILE_SIZE_MB) {
    errors.image = `File size exceeds ${MAX_FILE_SIZE_MB}MB. Please upload a smaller file.`;
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};

/**
 * Normalize categories data
 * @param {Array} categories - Categories array
 * @returns {Array} Normalized categories
 */
export const normalizeCategories = (categories) => {
  return categories.map((cat) => ({
    ...cat,
    name: typeof cat.name === "string" ? cat.name : cat.name?.en || "",
  }));
};

/**
 * Normalize subcategories data
 * @param {Array} subcategories - Subcategories array
 * @returns {Array} Normalized subcategories
 */
export const normalizeSubcategories = (subcategories) => {
  return subcategories.map((sub) => ({
    ...sub,
    subCat: typeof sub.subCat === "string" ? sub.subCat : sub.subCat?.en || "",
  }));
};

/**
 * Format date for input field
 * @param {string} date - Date string
 * @returns {string} Formatted date
 */
export const formatDateForInput = (date) => {
  if (!date) return "";
  return new Date(date).toISOString().split("T")[0];
};

/**
 * Build FormData for product submission
 * @param {Object} formData - Form data
 * @param {boolean} isEditing - Is editing mode
 * @param {string} id - Product ID
 * @returns {FormData} Form data object
 */
export const buildProductFormData = (formData, isEditing, id) => {
  const data = new FormData();

  // Append multilingual fields as JSON strings
  data.append("productName", JSON.stringify(formData.productName));
  data.append("description", JSON.stringify(formData.description));

  // Create variants array
  const variantsData = [
    {
      quantity: 1,
      unit: "piece",
      manufactureDate: formData.manufactureDate || null,
      expiryDate: formData.expiryDate || null,
      price: Number(formData.price),
      packageCharge:
        formData.packageCharge !== "" ? Number(formData.packageCharge) : 0,
      offerPrice:
        isEditing && id
          ? formData.offerPrice
            ? Number(formData.offerPrice)
            : null
          : null,
    },
  ];

  data.append("variants", JSON.stringify(variantsData));

  // Append other fields
  const fieldsToSkip = ["productName", "description", "image", "variants", "isTaxable"];
  // Always send includeTax (default true)
  data.append("isTaxable", formData.isTaxable ? "true" : "false");;

  Object.keys(formData).forEach((key) => {
    if (fieldsToSkip.includes(key)) return;

    const value = formData[key];
    if (value === undefined || value === null || value === "") return;

    if (typeof value === "boolean") {
      data.append(key, value ? "true" : "false");
    } else if (typeof value === "number") {
      data.append(key, String(value));
    } else {
      data.append(key, String(value));
    }
  });

  // Only append image if it's a File object
  if (formData.image && formData.image instanceof File) {
    data.append("image", formData.image);
  }

  // Add metadata
  data.append("username", localStorage.getItem("username"));

  return data;
};
